import React, { useEffect, useState } from 'react';
import ProfileHighlights from '@/features/profile/components/ProfileHighlights';
import AboutMeTab from '@/features/profile/components/AboutMeTab';
import MySocialMattTab from '@/features/profile/components/MySocialMattTab';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { TopNavigation } from '@/components/layout/TopNavigation';
import { BottomNavigation } from '@/components/layout/BottomNavigation';
import { EmptyState } from '@/components/ui/EmptyState';
import { User, AlertCircle } from 'lucide-react';
import { useI18n } from '@/contexts/I18nContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { useUserProfile } from '@/features/profile/hooks/useUserProfile';
import { PractitionerOnboarding } from '@/components/onboarding/PractitionerOnboarding';
import { InstructorOnboarding } from '@/components/onboarding/InstructorOnboarding';
import { SingleStepWizardModal, WizardStepType } from '@/components/sossial/SingleStepWizardModal';
import { EditLocationModal } from '@/features/profile/components/EditLocationModal';

const MyJourney: React.FC = () => {
  const { user } = useAuth();
  const { t } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<any>(null);

  // Reactive belt_rank via React Query — updates automatically after promotions change
  // (invalidated by useCreatePromotion / useUpdatePromotion / useDeletePromotion onSuccess)
  const { profile: reactiveProfile } = useUserProfile();
  const [academies, setAcademies] = useState<any[]>([]);
  const [interests, setInterests] = useState<string[]>([]);
  const [favoriteTechniques, setFavoriteTechniques] = useState<Record<string, string[]>>({});
  const [posts, setPosts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [techniqueCategories, setTechniqueCategories] = useState<string[]>([]);
  const [wizardType, setWizardType] = useState<WizardStepType | null>(null);
  const [locationOpen, setLocationOpen] = useState(false);

  // Refetch whenever the user navigates to this page (location.key changes on each navigation)
  useEffect(() => {
    if (user) {
      fetchData();
    }
    // eslint-disable-next-line
  }, [user, location.key]);

  const fetchData = async () => {
    setLoading(true);
    if (!user) {
      return;
    }
    // Fetch profile (with join for business_types)
    const { data: profileData, error: profileError } = await supabase
      .from('profiles')
      .select('*, business_type:business_types(name)')
      .eq('id', user.id)
      .single();
    
    if (profileError) {
      console.error('MyJourney - Profile fetch error:', profileError);
    }

    if (!profileData) {
      setLoading(false);
      return;
    }

    setProfile(profileData);

    // Fetch main linked academy
    let academyArr = [];
    
    if (profileData?.main_academy_id) {
      const { data: academyData, error: academyError } = await supabase
        .from('academies')
        .select('id, name')
        .eq('id', profileData.main_academy_id)
        .limit(1);
      
      if (academyError) {
        console.error('MyJourney - Academy fetch error:', academyError);
      }
      
      academyArr = academyData || [];
    }
    
    setAcademies(academyArr);

    // Fetch interests
    const { data: hobbyData } = await supabase
      .from('profile_hobbies')
      .select('hobby:hobbies(name)')
      .eq('profile_id', user.id);
    setInterests(hobbyData?.map((h: any) => h.hobby?.name).filter(Boolean) || []);

    // Fetch real technique categories
    const { data: categoriesData } = await supabase
      .from('technique_categories')
      .select('name');
    const categories = categoriesData?.map((c: any) => c.name) || [];
    setTechniqueCategories(categories);

    // Fetch favorite techniques grouped by category
    const { data: favTechData } = await supabase
      .from('profile_favorite_techniques')
      .select('technique:techniques(name, category:technique_categories(name))')
      .eq('profile_id', user.id);
    const grouped: Record<string, string[]> = {};
    categories.forEach((cat: string) => { grouped[cat] = []; });
    favTechData?.forEach((item: any) => {
      const cat = item.technique?.category?.name || 'Other';
      if (!grouped[cat]) grouped[cat] = [];
      if (item.technique?.name) grouped[cat].push(item.technique.name);
    });
    setFavoriteTechniques(grouped);

    // Fetch user's posts
    const { data: postData } = await supabase
      .from('posts')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(10);
    setPosts(postData || []);

    setLoading(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#160C0C] text-white text-lg">
        Loading profile...
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-[#160C0C]">
        <TopNavigation />
        <div className="pt-20 pb-20">
          <EmptyState
            icon={AlertCircle}
            title={t('profile.not_found')}
            subtitle={t('profile.not_found_description')}
            className="text-white"
          />
        </div>
        <BottomNavigation />
      </div>
    );
  }

  // Trigger onboarding for new users who haven't completed it yet
  if (!profile.optional_fields_completed) {
    const handleOnboardingComplete = (destination = '/calendar') => {
      navigate(destination, { replace: true });
    };
    if (profile.is_instructor) {
      return (
        <div className="min-h-screen bg-sossiety-dark">
          <InstructorOnboarding username={profile.username} onComplete={handleOnboardingComplete} />
        </div>
      );
    }
    return (
      <div className="min-h-screen bg-sossiety-dark">
        <PractitionerOnboarding username={profile.username} onComplete={handleOnboardingComplete} />
      </div>
    );
  }

  const handleOpenWizard = (type: WizardStepType | 'location') => {
    if (type === 'location') {
      setLocationOpen(true);
    } else {
      setWizardType(type);
    }
  };

  const handleLocationSave = async (data: { country: string; state: string; city: string }) => {
    if (!user) return;
    const { error } = await supabase
      .from('profiles')
      .update({ country: data.country, state: data.state, city: data.city })
      .eq('id', user.id);
    if (error) throw error;
    setLocationOpen(false);
    fetchData();
  };

  return (
    <>
      <TopNavigation />
      <div className="relative min-h-screen w-full bg-[#160C0C] pb-20 pt-20">
        <div className="px-4">
          <ProfileHighlights
            fullName={profile.full_name || ''}
            username={profile.username || ''}
            belt={reactiveProfile?.belt_rank || profile.belt_rank || ''}
            beltColor={profile.belt_color || '#fff'}
            academies={academies}
            location={{ country: profile.country, state: profile.state, city: profile.city }}
            social={{ instagram: profile.instagram, x: profile.x, facebook: profile.facebook, youtube: profile.youtube }}
            website={profile.website}
            interests={interests}
            profession={profile.profession || ''}
            favoriteTechniques={favoriteTechniques}
            techniqueCategories={techniqueCategories}
            businessType={profile.business_type?.name}
            linkedin={profile.linkedin}
            email={user.email}
            profilePictureUrl={profile.profile_picture_url}
            userId={user.id}
            isOwnProfile={true}
            editable={true}
            onProfileUpdated={fetchData}
            isInstructor={profile.is_instructor || false}
            onOpenWizard={handleOpenWizard}
          />
        </div>
        <BottomNavigation />
      </div>

      {/* Profile signal wizards */}
      <SingleStepWizardModal
        open={wizardType !== null}
        type={wizardType}
        onClose={() => setWizardType(null)}
        onSaved={fetchData}
      />
      <EditLocationModal
        open={locationOpen}
        onClose={() => setLocationOpen(false)}
        onSave={handleLocationSave}
        initialData={{ country: profile.country || '', state: profile.state || '', city: profile.city || '' }}
      />
    </>
  );
};

export default MyJourney; 